import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "@cieda/djayma-ui";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfDay, endOfDay, isSameDay, isSameMonth, isWithinInterval, addMonths, subMonths, getDaysInMonth, getDay } from "date-fns";
import { fr } from "date-fns/locale";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRange {
  readonly from: Date;
  readonly to: Date;
}

export interface DateRangePickerProps {
  readonly value: DateRange;
  readonly onChange: (range: DateRange) => void;
  readonly presets?: readonly string[];
}

// ---------------------------------------------------------------------------
// Default presets
// ---------------------------------------------------------------------------

const DEFAULT_PRESETS = [
  "Aujourd'hui",
  "Cette semaine",
  "Ce mois",
  "Ce trimestre",
] as const;

function resolvePreset(label: string): DateRange {
  const now = new Date();
  switch (label) {
    case "Aujourd'hui":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "Cette semaine":
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
    case "Ce mois":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "Ce trimestre":
      return { from: startOfQuarter(now), to: endOfQuarter(now) };
    default:
      return { from: startOfDay(now), to: endOfDay(now) };
  }
}

// ---------------------------------------------------------------------------
// Mini calendar
// ---------------------------------------------------------------------------

function MiniCalendar({
  month,
  range,
  selectionStart,
  onDayClick,
  onPrevMonth,
  onNextMonth,
}: {
  month: Date;
  range: DateRange | null;
  selectionStart: Date | null;
  onDayClick: (day: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) {
  const daysInMonth = getDaysInMonth(month);
  const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  // Monday = 0 offset
  const startOffset = (getDay(firstDayOfMonth) + 6) % 7;

  const dayNames = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          onClick={onPrevMonth}
          className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-stone-900">
          {format(month, "MMMM yyyy", { locale: fr })}
        </span>
        <button
          type="button"
          onClick={onNextMonth}
          className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0">
        {dayNames.map((d) => (
          <div key={d} className="py-1 text-center text-xs font-medium text-stone-400">
            {d}
          </div>
        ))}

        {/* Empty cells for offset */}
        {Array.from({ length: startOffset }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Day cells */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = new Date(month.getFullYear(), month.getMonth(), i + 1);
          const isInRange =
            range &&
            isWithinInterval(day, { start: range.from, end: range.to });
          const isStart = range && isSameDay(day, range.from);
          const isEnd = range && isSameDay(day, range.to);
          const isSelected = selectionStart && isSameDay(day, selectionStart);

          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(day)}
              className={`
                h-8 w-8 rounded text-xs transition-colors
                ${isStart || isEnd ? "bg-amber-600 font-semibold text-white" : ""}
                ${isInRange && !isStart && !isEnd ? "bg-amber-100 text-amber-900" : ""}
                ${isSelected && !isStart && !isEnd ? "ring-2 ring-amber-400" : ""}
                ${!isInRange && !isSelected ? "text-stone-700 hover:bg-stone-100" : ""}
                ${!isSameMonth(day, month) ? "text-stone-300" : ""}
              `}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DateRangePicker({
  value,
  onChange,
  presets,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(value.from);
  const [selectionStart, setSelectionStart] = useState<Date | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const activePresets = useMemo(
    () => presets ?? DEFAULT_PRESETS,
    [presets],
  );

  // Close on outside click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setOpen(false);
      setSelectionStart(null);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, handleOutsideClick]);

  const handleDayClick = (day: Date) => {
    if (!selectionStart) {
      setSelectionStart(day);
    } else {
      const from = day < selectionStart ? day : selectionStart;
      const to = day < selectionStart ? selectionStart : day;
      onChange({ from: startOfDay(from), to: endOfDay(to) });
      setSelectionStart(null);
      setOpen(false);
    }
  };

  const handlePreset = (label: string) => {
    onChange(resolvePreset(label));
    setSelectionStart(null);
    setOpen(false);
  };

  const displayLabel = `${format(value.from, "dd/MM/yyyy")} - ${format(value.to, "dd/MM/yyyy")}`;

  return (
    <div ref={popoverRef} className="relative inline-block">
      <Button
        variant="outline"
        onClick={() => setOpen((prev) => !prev)}
        className="gap-2 text-sm"
      >
        <Calendar className="h-4 w-4 text-stone-400" />
        {displayLabel}
      </Button>

      {open && (
        <div className="absolute left-0 z-20 mt-1 rounded-lg border border-stone-200 bg-white p-4 shadow-lg">
          {/* Presets */}
          <div className="mb-3 flex flex-wrap gap-1.5">
            {activePresets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePreset(preset)}
                className="rounded-full border border-stone-200 px-3 py-1 text-xs font-medium text-stone-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700"
              >
                {preset}
              </button>
            ))}
          </div>

          {/* Calendar */}
          <MiniCalendar
            month={month}
            range={selectionStart ? null : value}
            selectionStart={selectionStart}
            onDayClick={handleDayClick}
            onPrevMonth={() => setMonth((m) => subMonths(m, 1))}
            onNextMonth={() => setMonth((m) => addMonths(m, 1))}
          />
        </div>
      )}
    </div>
  );
}
